import { useState, useEffect } from 'react';
import { Clock, BarChart3, FileText, Settings, Menu, ChevronLeft, X, Smartphone } from 'lucide-react';
import { motion } from 'motion/react';
import logo from '../../assets/logo.png'
import { Button } from '../ui/button';
import { useAuth } from '../../graphql/hooks/useAuth';

interface SidebarProps {
  activePage: string;
  onPageChange: (page: string) => void;
}

export function Sidebar({ activePage, onPageChange }: SidebarProps) {
  const { userType } = useAuth()
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
      if (window.innerWidth >= 1024) {
        setIsMobileOpen(false);
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Build menu items - show user navigation with admin option for all users
  const getMenuItems = () => {
    const items = [
      { id: 'zeiterfassung', label: 'Zeiterfassung', icon: Clock },
      { id: 'auswertung', label: 'Auswertung', icon: BarChart3 },
      {
        id: 'meine-anträge',
        label: 'Meine Anträge',
        icon: FileText,
      },
      // {
      //   id: 'apps',
      //   label: 'Apps',
      //   icon: Smartphone,
      // },
    ];

    if ((userType === 'admin' || userType === 'Superadmin') && !isMobile) {
      items.push({
        id: 'admin',
        label: 'Admin',
        icon: Settings,
      });
    }

    return items;
  };

  const menuItems = getMenuItems();

  const handlePageChange = (page: string) => {
    onPageChange(page);
    if (isMobile) {
      setIsMobileOpen(false);
    }
  };

  const sidebarContent = (
    <>
      {/* Logo Section */}
      <div className="h-[140.5px] border-b border-[#2a2a2a] flex items-center justify-center px-6">
        {!isCollapsed && (
          <img src={logo} className='w-full'/>
        )}
        {isCollapsed && (
          <div className="text-white text-[24px] font-bold">E</div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 pt-4 space-y-2">
        {menuItems.map((item, index) => {
          const Icon = item.icon;
          const isActive = activePage === item.id;

          return (
            <motion.button
              key={item.id}
              onClick={() => handlePageChange(item.id)}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ 
                delay: index * 0.05,
                type: "spring",
                stiffness: 300,
                damping: 20
              }}
              whileHover={{ 
                scale: 1.05,
                x: 5,
                transition: { type: "spring", stiffness: 400, damping: 10 }
              }}
              whileTap={{ 
                scale: 0.95,
                transition: { type: "spring", stiffness: 500, damping: 15 }
              }}
              className={`w-full flex items-center gap-3 px-3 h-12 rounded-[10px] transition-colors relative overflow-hidden ${
                isActive
                  ? 'bg-[#0084c7] text-white'
                  : 'text-[#e9e9e9] hover:bg-[#0084c7]/10'
              }`}
            >
              {isActive && (
                <motion.div
                  layoutId="activeIndicator"
                  className="absolute inset-0 bg-[#0084c7] rounded-[10px]"
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                />
              )}
              <motion.div
                className="relative z-10"
                animate={{ rotate: isActive ? [0, -10, 10, -10, 0] : 0 }}
                transition={{ duration: 0.5 }}
              >
                <Icon className="size-5 shrink-0" />
              </motion.div>
              {!isCollapsed && (
                <span className="text-[16px] relative z-10">{item.label}</span>
              )}
            </motion.button>
          );
        })}
      </nav>

      {/* Collapse Button */}
      {!isMobile && (
        <div className="border-t border-[#2a2a2a] p-4">
          <Button
            onClick={() => setIsCollapsed(!isCollapsed)}
            variant="ghost"
            className="w-full flex items-center gap-3 px-3 h-12 rounded-[10px] text-[#e9e9e9] hover:bg-[#0084c7]/10"
          >
            {isCollapsed ? (
              <>
                <Menu className="size-5" />
              </>
            ) : (
              <>
                <ChevronLeft className="size-5" />
                <span className="text-[16px]">Menü einklappen</span>
              </>
            )}
          </Button>
        </div>
      )}
    </>
  );

  // Mobile overlay and drawer
  if (isMobile) {
    return (
      <>
        {/* Mobile menu button */}
        <button
          onClick={() => setIsMobileOpen(true)}
          className="lg:hidden fixed h-10 items-center top-11 left-8 z-50 bg-[#01071a] text-white p-2 rounded-[10px] shadow-lg"
        >
          <Menu className="size-6" />
        </button>

        {/* Overlay */}
        {isMobileOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setIsMobileOpen(false)}
          />
        )}

        {/* Mobile drawer */}
        <div
          className={`fixed top-0 left-0 h-screen bg-gradient-to-b from-[#01071a] to-[#05203f] w-[256px] z-50 transform transition-transform duration-300 lg:hidden ${
            isMobileOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <button
            onClick={() => setIsMobileOpen(false)}
            className="absolute top-4 right-4 text-white p-2"
          >
            <X className="size-6" />
          </button>
          <div className="flex flex-col h-full">{sidebarContent}</div>
        </div>
      </>
    );
  }

  // Desktop sidebar
  return (
    <div
      className={`bg-gradient-to-b from-[#01071a] to-[#05203f] h-[calc(100vh-32px)] flex flex-col transition-all duration-300 m-4 rounded-[16px] shadow-xl ${
        isCollapsed ? 'w-[80px]' : 'w-[256px]'
      }`}
    >
      {sidebarContent}
    </div>
  );
}